import * as Sentry from '@sentry/react';

/**
 * A route chunk that can't be fetched or evaluated. Every browser words it differently, and the
 * MIME variant is what a missing hashed asset served an `index.html` fallback produces.
 */
const CHUNK_LOAD_MESSAGES = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /expected a javascript(-or-wasm)? module script/i,
];

export type RouteErrorKind = 'chunk_load' | 'render';

export type RouteErrorContext = {
  /** The URL the router had committed when the boundary caught. */
  pathname: string;
  /** Route id of the match that threw, e.g. `/_auth/care-plan/$patientId`. */
  routeId?: string;
};

const errorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : typeof error === 'string' ? error : '';

export const classifyRouteError = (error: unknown): RouteErrorKind =>
  CHUNK_LOAD_MESSAGES.some((pattern) => pattern.test(errorMessage(error))) ? 'chunk_load' : 'render';

/**
 * Reports an error caught by a router error boundary to Sentry with the route that failed.
 *
 * Errors thrown while a route loads or renders are caught inside React's render phase, so they never
 * reach `window.onerror` and Sentry's global handlers never see them — without this they are
 * invisible in production. Wire it up as the router's `defaultOnCatch`.
 *
 * Reported as unhandled: a caught route error leaves the user on a dead page, so it should count
 * against crash-free sessions rather than read as something the app recovered from.
 */
export const captureRouteError = (
  error: unknown,
  errorInfo: { componentStack?: string | null },
  { pathname, routeId }: RouteErrorContext,
): void => {
  const kind = classifyRouteError(error);

  Sentry.captureReactException(error, errorInfo, {
    captureContext: {
      contexts: { route: { pathname, routeId } },
      tags: { 'route.error_kind': kind, 'route.id': routeId },
      // Chunk-load messages carry a content-hashed asset URL, so default grouping would open a
      // fresh issue on every deploy. Collapse the class into one issue and break it down by
      // `route.id`. Render throws keep their stack-trace grouping.
      ...(kind === 'chunk_load' ? { fingerprint: ['route-chunk-load-failure'] } : {}),
    },
    mechanism: { handled: false, type: 'react.tanstack_router.catch_boundary' },
  });
};
